
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GeneralSettings } from "@/components/settings/GeneralSettings";
import { AppearanceSettings } from "@/components/settings/AppearanceSettings";
import { EmployeeNotificationPreferences } from "@/components/settings/EmployeeNotificationPreferences";
import { CurrenciesTable } from "@/components/settings/CurrenciesTable";
import { DatabaseSettings } from "@/components/settings/DatabaseSettings";
import { SidebarSettings } from "@/components/settings/SidebarSettings";
import { MenuReorderSection } from "@/components/settings/MenuReorderSection";
import { Settings as SettingsIcon, Palette, DollarSign, User, Database, Menu, BarChart, MapPin, Activity } from "lucide-react";
import { GoogleAnalyticsSettings } from "@/components/analytics/GoogleAnalyticsSettings";
import { HeaderSettings } from "@/components/settings/HeaderSettings";
import { LocationSettings } from "@/components/settings/LocationSettings";

export default function Settings() {
  return (
    <div className="w-full" dir="rtl">
      <div className="space-y-4 sm:space-y-6">
        {/* Enhanced Header with Gradient and Animation */}
        <div className="flex items-center gap-3 sm:gap-4 animate-fade-in-content">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-xl sm:rounded-2xl blur-lg sm:blur-xl"></div>
            <div className="relative bg-gradient-to-br from-primary to-primary-hover p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg">
              <SettingsIcon className="h-6 w-6 sm:h-7 sm:w-7 md:h-9 md:w-9 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
              الإعدادات
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">إدارة وتخصيص إعدادات النظام</p>
          </div>
        </div>

        <Tabs defaultValue="general" className="space-y-4 sm:space-y-6 w-full">
          <div className="w-full overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0 custom-scrollbar">
            <TabsList className="inline-flex w-full min-w-max h-auto p-1 sm:p-1.5 gap-1 sm:gap-2 bg-muted/50 rounded-xl sm:rounded-2xl shadow-md backdrop-blur-sm border border-border/50">
              <TabsTrigger 
                value="general" 
                className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 whitespace-nowrap rounded-lg sm:rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-general))] data-[state=active]:to-[hsl(var(--tab-general))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-general))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <SettingsIcon className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5" style={{ color: 'hsl(var(--tab-general))' }} />
                <span className="hidden sm:inline font-medium">عام</span>
              </TabsTrigger>
              <TabsTrigger 
                value="appearance" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-appearance))] data-[state=active]:to-[hsl(var(--tab-appearance))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-appearance))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <Palette className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-appearance))' }} />
                <span className="hidden sm:inline font-medium">المظهر</span>
              </TabsTrigger>
              <TabsTrigger 
                value="personal-notifications" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-notifications))] data-[state=active]:to-[hsl(var(--tab-notifications))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-notifications))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <User className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-notifications))' }} />
                <span className="hidden sm:inline font-medium">الإشعارات</span>
              </TabsTrigger>
              <TabsTrigger 
                value="header-settings" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-header))] data-[state=active]:to-[hsl(var(--tab-header))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-header))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <Menu className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-header))' }} />
                <span className="hidden sm:inline font-medium">الشريط</span>
              </TabsTrigger>
              <TabsTrigger 
                value="currencies" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-currencies))] data-[state=active]:to-[hsl(var(--tab-currencies))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-currencies))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-currencies))' }} />
                <span className="hidden sm:inline font-medium">العملات</span>
              </TabsTrigger>
              <TabsTrigger 
                value="database" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-database))] data-[state=active]:to-[hsl(var(--tab-database))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-database))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <Database className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-database))' }} />
                <span className="hidden sm:inline font-medium">البيانات</span>
              </TabsTrigger>
              <TabsTrigger 
                value="sidebar" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-sidebar))] data-[state=active]:to-[hsl(var(--tab-sidebar))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-sidebar))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <Menu className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-sidebar))' }} />
                <span className="hidden sm:inline font-medium">القائمة</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="flex items-center gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[hsl(var(--tab-analytics))] data-[state=active]:to-[hsl(var(--tab-analytics))]/80 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[hsl(var(--tab-analytics))]/30 data-[state=active]:scale-105 hover:bg-accent/50 hover:scale-105"
              >
                <BarChart className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'hsl(var(--tab-analytics))' }} />
                <span className="hidden sm:inline font-medium">تحليلات</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general" className="animate-fade-in-content">
            <GeneralSettings />
          </TabsContent>

          <TabsContent value="appearance" className="animate-fade-in-content">
            <AppearanceSettings />
          </TabsContent>

          <TabsContent value="personal-notifications" className="animate-fade-in-content">
            <EmployeeNotificationPreferences />
          </TabsContent>

          <TabsContent value="header-settings" className="animate-fade-in-content">
            <HeaderSettings />
          </TabsContent>


          <TabsContent value="currencies" className="animate-fade-in-content">
            <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10">
              <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-currencies))]/20 to-transparent">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-currencies))] to-[hsl(var(--tab-currencies))]/80 shadow-lg shadow-[hsl(var(--tab-currencies))]/30">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">إدارة العملات</span>
                </CardTitle>
                <CardDescription className="text-base">
                  إضافة وتحديث أسعار صرف العملات المختلفة
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <CurrenciesTable />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="database" className="animate-fade-in-content">
            <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10">
              <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-database))]/20 to-transparent">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-database))] to-[hsl(var(--tab-database))]/80 shadow-lg shadow-[hsl(var(--tab-database))]/30">
                    <Database className="h-5 w-5 text-white" />
                  </div>
                  <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">إعدادات قاعدة البيانات</span>
                </CardTitle>
                <CardDescription className="text-base">
                  تغيير إعدادات الاتصال بقاعدة البيانات وإدارة النسخ المتعددة
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <DatabaseSettings />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sidebar" className="space-y-6 animate-fade-in-content">
            <SidebarSettings />
            <MenuReorderSection />
            
            {/* شرح مقاييس الأداء */}
            <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10">
              <CardHeader className="border-b border-gradient-to-r from-transparent via-primary/20 to-transparent">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary-hover shadow-lg shadow-primary/30">
                    <Activity className="h-5 w-5 text-white" />
                  </div>
                  <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">شرح مقاييس الأداء الذكي</span>
                </CardTitle>
                <CardDescription className="text-base">
                  فهم المؤشرات والمقاييس المستخدمة في نظام مراقبة الأداء
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-4">
                    <div className="p-3 sm:p-4 border border-success/30 bg-gradient-to-br from-background to-success/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                      <h4 className="font-semibold text-success mb-2 text-sm sm:text-base flex items-center gap-2">
                        📈 معدل التحويل
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                        النسبة المئوية للعملاء الذين تم تحويلهم من عملاء محتملين إلى عملاء مؤكدين
                      </p>
                      <div className="text-xs text-muted-foreground/80">
                        مثال: من 100 عميل، 30 أكدوا الحجز = معدل تحويل 30%
                      </div>
                    </div>
                    
                    <div className="p-3 sm:p-4 border border-info/30 bg-gradient-to-br from-background to-info/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                      <h4 className="font-semibold text-info mb-2 text-sm sm:text-base flex items-center gap-2">
                        ⏱️ وقت الاستجابة
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                        متوسط الوقت المطلوب للرد على استفسارات العملاء أو معالجة طلباتهم
                      </p>
                      <div className="text-xs text-muted-foreground/80">
                        الهدف: أقل من 60 دقيقة للاستجابة الأولى
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-3 sm:p-4 border border-warning/30 bg-gradient-to-br from-background to-warning/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                      <h4 className="font-semibold text-warning mb-2 text-sm sm:text-base flex items-center gap-2">
                        💼 حمولة العمل
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                        النسبة المئوية لاستغلال طاقة الفريق من إجمالي الطاقة المتاحة
                      </p>
                      <div className="text-xs text-muted-foreground/80">
                        85%+ = حمولة عالية، 50-85% = متوسط، أقل من 50% = منخفض
                      </div>
                    </div>
                    
                    <div className="p-3 sm:p-4 border border-primary/30 bg-gradient-to-br from-background to-primary/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                      <h4 className="font-semibold text-primary mb-2 text-sm sm:text-base flex items-center gap-2">
                        📋 المهام النشطة
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                        عدد المهام والطلبات التي يتم العمل عليها حالياً
                      </p>
                      <div className="text-xs text-muted-foreground/80">
                        يشمل العملاء الجدد، الطلبات قيد المعالجة، والمتابعات المطلوبة
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 sm:mt-6 p-4 sm:p-5 bg-gradient-to-br from-info/10 to-info/5 border border-info/20 rounded-xl shadow-md">
                  <h5 className="font-semibold text-info mb-3 text-sm sm:text-base flex items-center gap-2">
                    💡 كيفية تحسين المقاييس:
                  </h5>
                  <ul className="text-xs sm:text-sm text-foreground/80 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-info mt-0.5">•</span>
                      <span><strong className="text-foreground">معدل التحويل:</strong> تحسين العروض، متابعة العملاء بانتظام، تدريب فريق المبيعات</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-info mt-0.5">•</span>
                      <span><strong className="text-foreground">وقت الاستجابة:</strong> استخدام قوالب جاهزة، توزيع العمل بذكاء، أتمتة الردود الأولية</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-info mt-0.5">•</span>
                      <span><strong className="text-foreground">حمولة العمل:</strong> إعادة توزيع المهام، تحسين العمليات، زيادة الفريق عند الحاجة</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-info mt-0.5">•</span>
                      <span><strong className="text-foreground">المهام النشطة:</strong> ترتيب الأولويات، تسريع المعالجة، تحسين سير العمل</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="analytics" className="space-y-6 animate-fade-in-content">
            <GoogleAnalyticsSettings />
            <LocationSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
